/**
 * Generate a UUID v4
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Compact fallback for older browsers
  let d = Date.now();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * SHA-256 hash function with fallback
 */
export async function sha256(str: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
    try {
      const buf = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(str),
      );
      return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {}
  }

  // Fallback: simple hash (good enough for fingerprinting)
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

/**
 * Parse URL and extract components
 */
export function parseUrl(url: string): {
  path: string;
  search: Record<string, string>;
} {
  try {
    const u = new URL(url);
    const search: Record<string, string> = {};
    u.searchParams.forEach((v, k) => {
      search[k] = v;
    });
    return { path: u.pathname, search };
  } catch {
    return { path: '/', search: {} };
  }
}

/**
 * Get referrer source from URL
 * Returns the domain name for ANY external referrer
 */
export function getReferrerSource(referrer: string): string | undefined {
  if (!referrer) return;

  try {
    const referrerHost = new URL(referrer).hostname.toLowerCase();

    // Skip if same domain
    if (referrerHost === location.hostname.toLowerCase()) return;

    // Return the clean hostname (remove www. prefix if present)
    return referrerHost.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

/**
 * Simple debounce function
 */
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
): T {
  let timeout: any;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  }) as T;
}

/**
 * Check if we're in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Debug logging
 */
export function debugLog(message: string, data?: any): void {
  if (typeof console !== 'undefined' && console.log) {
    if (data !== undefined) {
      console.log(`[Pulsora] ${message}`, data);
    } else {
      console.log(`[Pulsora] ${message}`);
    }
  }
}
